import type {
  Dapp,
  Publisher as PublisherDto,
  Release as ReleaseDto,
} from '@src/dto';
import { Coins } from 'lucide-react';
import type { ReactNode } from 'react';
import type { LooseNftMetadata } from '../../types/api';
import { buildReleaseNftName } from '../../utils/releaseNftName';
import { Button } from '../Button';
import { PortalCard } from '../portal';
import { ReleaseNftMintModal } from '../ReleaseNftMintModal';
import { VerifyCollectionButton } from '../VerifyCollectionButton';
import { BuildSection } from './components/BuildSection';
import { NftInformationSection } from './components/NftInformationSection';
import { PermissionsSection } from './components/PermissionsSection';
import { ResubmitHubspotButton } from './ResubmitHubspotButton';

interface ReleaseLoadedContentProps {
  release: ReleaseDto;
  dapp: Dapp | null;
  publisher: PublisherDto | null;
  nftMetadata: LooseNftMetadata | null;
  apkDownloadUrl: string | null;
  arweaveApkUrl: string | null;
  canManageReleaseArtifacts: boolean;
  canSubmitReleaseToStore: boolean;
  showMintModal: boolean;
  onReloadRelease: () => void | Promise<void>;
  onMintClick: () => void;
  onMintClose: () => void;
  onMintSuccess: (mintAddress: string, txSignature: string) => void;
  onVerificationSuccess: () => Promise<void>;
}

export function formatReleaseVersionTitle(versionName: string | null) {
  if (!versionName) return 'Unknown version';
  return /^v\d/i.test(versionName) ? versionName : `v${versionName}`;
}

/**
 * Body of the release page (the header lives in `ReleaseDetailPage` so it stays
 * mounted across loading). Fades in once when the release first lands.
 */
export function ReleaseLoadedContent({
  release,
  dapp,
  publisher,
  nftMetadata,
  apkDownloadUrl,
  arweaveApkUrl,
  canManageReleaseArtifacts,
  canSubmitReleaseToStore,
  showMintModal,
  onReloadRelease,
  onMintClick,
  onMintClose,
  onMintSuccess,
  onVerificationSuccess,
}: ReleaseLoadedContentProps) {
  return (
    <>
      <div className="motion-enter flex flex-col gap-4">
        <ReleaseActions
          release={release}
          dapp={dapp}
          canManageReleaseArtifacts={canManageReleaseArtifacts}
          canSubmitReleaseToStore={canSubmitReleaseToStore}
          onReloadRelease={onReloadRelease}
          onMintClick={onMintClick}
          onVerificationSuccess={onVerificationSuccess}
        />

        <BuildSection
          release={release}
          apkDownloadUrl={apkDownloadUrl}
          arweaveApkUrl={arweaveApkUrl}
        />

        <NftInformationSection
          release={release}
          dapp={dapp}
          nftMetadata={nftMetadata}
        />

        <PermissionsSection permissions={release.permissions || []} />
      </div>

      <ReleaseMintModalMount
        release={release}
        dapp={dapp}
        publisher={publisher}
        showMintModal={showMintModal}
        onMintClose={onMintClose}
        onMintSuccess={onMintSuccess}
      />
    </>
  );
}

/** Callout card for a pending on-chain step (mint / verify). */
function ActionCallout({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <PortalCard className="flex flex-wrap items-center justify-between gap-4 p-6">
      <div className="min-w-0">
        <h3 className="text-sm font-bold text-white">{title}</h3>
        <p className="mt-0.5 text-sm text-seeker-400">{description}</p>
      </div>
      {children}
    </PortalCard>
  );
}

interface ReleaseActionsProps {
  release: ReleaseDto;
  dapp: Dapp | null;
  canManageReleaseArtifacts: boolean;
  canSubmitReleaseToStore: boolean;
  onReloadRelease: () => void | Promise<void>;
  onMintClick: () => void;
  onVerificationSuccess: () => Promise<void>;
}

function ReleaseActions({
  release,
  dapp,
  canManageReleaseArtifacts,
  canSubmitReleaseToStore,
  onReloadRelease,
  onMintClick,
  onVerificationSuccess,
}: ReleaseActionsProps) {
  return (
    <>
      {canSubmitReleaseToStore && (
        <ResubmitHubspotButton
          releaseId={release.id}
          dappName={dapp?.dappName || ''}
          whatsNew={release.newInVersion || ''}
          isProcessed={release.status === 'Processed'}
          hasNft={!!release.nftMintAddress}
          hubspotTicketId={release.hubspotTicketId}
          isCollectionVerified={release.isCollectionVerified}
          submissionStatus={release.submissionStatus}
          onSuccess={onReloadRelease}
        />
      )}

      {release.status === 'Processed' &&
        canManageReleaseArtifacts &&
        !release.nftMintAddress &&
        dapp?.nftMintAddress && (
          <ActionCallout
            title="Ready to publish on-chain"
            description="Mint a Release NFT to publish this version on Solana."
          >
            <Button variant="primary" onClick={onMintClick}>
              <Coins className="size-4" />
              Mint NFT
            </Button>
          </ActionCallout>
        )}

      {release.nftMintAddress &&
        canManageReleaseArtifacts &&
        dapp?.nftMintAddress &&
        dapp?.walletAddress &&
        !release.isCollectionVerified && (
          <ActionCallout
            title="Collection verification required"
            description="Verify this Release NFT as part of the App collection."
          >
            <VerifyCollectionButton
              dappId={dapp.id}
              nftMintAddress={release.nftMintAddress}
              collectionMintAddress={dapp.nftMintAddress}
              collectionAuthority={dapp.walletAddress}
              onSuccess={onVerificationSuccess}
              variant="outlineWhite"
              releaseId={release.id}
            />
          </ActionCallout>
        )}
    </>
  );
}

interface ReleaseMintModalMountProps {
  release: ReleaseDto;
  dapp: Dapp | null;
  publisher: PublisherDto | null;
  showMintModal: boolean;
  onMintClose: () => void;
  onMintSuccess: (mintAddress: string, txSignature: string) => void;
}

function ReleaseMintModalMount({
  release,
  dapp,
  publisher,
  showMintModal,
  onMintClose,
  onMintSuccess,
}: ReleaseMintModalMountProps) {
  if (!showMintModal || !dapp || !publisher) {
    return null;
  }

  return (
    <ReleaseNftMintModal
      isOpen={showMintModal}
      onClose={onMintClose}
      onSuccess={onMintSuccess}
      releaseData={{
        id: release.id,
        releaseName: buildReleaseNftName({
          localizedName: release.localizedName,
          dappName: dapp.dappName,
          versionName: release.versionName,
        }),
        releaseVersion: release.versionName || 'Unknown',
        androidPackage: release.androidPackage || '',
        versionCode: release.versionCode || 0,
        permissions: release.permissions || [],
        minSdk: release.minSdkVersion || 0,
        targetSdk: release.targetSdkVersion,
        certificateFingerprint: release.certificateFingerprint || '',
        releaseFileUrl: release.releaseFileUrl,
        releaseFileName: release.releaseFileName,
        releaseFileSize: release.releaseFileSize,
        releaseFileHash: release.releaseFileHash || '',
        publisherAddress: dapp.walletAddress || '',
        appMintAddress: dapp.nftMintAddress || '',
        // Release metadata
        shortDescription: release.shortDescription ?? undefined,
        longDescription: release.longDescription ?? undefined,
        newInVersion: release.newInVersion ?? undefined,
        localizedName: release.localizedName ?? undefined,
        locales: release.locales ?? undefined,
      }}
      dappData={{
        id: dapp.id,
        dappName: dapp.dappName,
        subtitle: dapp.subtitle ?? undefined,
        description: dapp.description,
        dappIconUrl: dapp.dappIconUrl ?? undefined,
        dappIconThumbnailUrl: dapp.dappIconThumbnailUrl ?? undefined,
        bannerUrl: dapp.bannerUrl ?? undefined,
        featureGraphicUrl: dapp.featureGraphicUrl ?? undefined,
        editorsChoiceGraphicUrl: dapp.editorsChoiceGraphicUrl ?? undefined,
        dappPreviewUrls: dapp.dappPreviewUrls || [],
        // Publisher details
        publisherName: dapp.publisherName ?? undefined,
        publisherWebsite: dapp.publisherWebsite ?? undefined,
        publisherEmail: dapp.publisherEmail ?? undefined,
        contactEmail: dapp.contactEmail ?? undefined,
        supportEmail: dapp.supportEmail ?? undefined,
        // Legal URLs
        licenseUrl: (dapp.licenseUrl ?? undefined) as string | undefined,
        copyrightUrl: (dapp.copyrightUrl ?? undefined) as string | undefined,
        privacyPolicyUrl: (dapp.privacyPolicyUrl ?? undefined) as
          | string
          | undefined,
      }}
    />
  );
}
